import type { ElectionState, ResultCandidate, StateCandidate, StatePosition, StateRace } from '../types';

export const RESULT_LABEL = { WINNER: 'Ganador', NO_WINNER: 'Sin ganador', TIE: 'Empate' } as const;

export function racesOfPosition(state: ElectionState, positionId: string): StateRace[] {
  return state.races
    .filter((r) => r.position_id === positionId)
    .sort((a, b) => a.round_number - b.round_number);
}

export function lastRace(races: StateRace[]): StateRace | undefined {
  return races[races.length - 1];
}

/** Candidates that can still be put in a new round */
export function activeCandidates(state: ElectionState): StateCandidate[] {
  return state.candidates.filter((c) => c.status === 'ACTIVE');
}

/**
 * Candidate ids suggested for the next round of a position:
 *  - first round: all active candidates;
 *  - later rounds: the top `topX` of the previous round (default: all that took part, so the
 *    room decides how many to drop), minus anyone no longer active.
 */
export function suggestedCandidateIds(state: ElectionState, positionId: string, topX?: number): string[] {
  const active = new Set(activeCandidates(state).map((c) => c.id));
  const previous = lastRace(racesOfPosition(state, positionId));
  if (!previous?.result_data) return activeCandidates(state).map((c) => c.id);
  const ranked = [...previous.result_data.candidates].sort((a, b) => b.votes - a.votes);
  // A tie: the room votes again only between the tied candidates (unless it asked for a specific top X)
  const tied = previous.result === 'TIE' && !topX ? ranked.filter((c) => c.votes === ranked[0].votes) : null;
  const taken = tied ?? (topX ? ranked.slice(0, topX) : ranked);
  return taken.map((c) => c.candidate_id).filter((id) => active.has(id));
}

/** What the admin can do next with a position */
export type PositionStep =
  | { kind: 'FILLED' }
  | { kind: 'CONFIRM_WINNER'; race: StateRace }
  | { kind: 'ROUND_OPEN'; race: StateRace }
  | { kind: 'ROUND_DRAFT'; race: StateRace }
  | { kind: 'NEW_ROUND'; first: boolean };

export function positionStep(state: ElectionState, position: StatePosition): PositionStep {
  if (position.status === 'FILLED') return { kind: 'FILLED' };
  const races = racesOfPosition(state, position.id);
  const last = lastRace(races);
  if (!last) return { kind: 'NEW_ROUND', first: true };
  if (last.status === 'OPEN') return { kind: 'ROUND_OPEN', race: last };
  if (last.status === 'DRAFT') return { kind: 'ROUND_DRAFT', race: last };
  if (last.result === 'WINNER' && !last.winner_confirmed_at) return { kind: 'CONFIRM_WINNER', race: last };
  return { kind: 'NEW_ROUND', first: false };
}

export function candidateName(state: ElectionState, id: string | null | undefined): string {
  if (!id) return 'Voto en blanco';
  return state.candidates.find((c) => c.id === id)?.full_name ?? '—';
}

export function sortedResult(race: StateRace): ResultCandidate[] {
  return [...(race.result_data?.candidates ?? [])].sort((a, b) => b.votes - a.votes);
}

/** Seconds left until `closesAt`, measured against the server clock */
export function secondsLeft(closesAt: string | null, serverNow: string, fetchedAtMs: number): number | null {
  if (!closesAt) return null;
  const drift = new Date(serverNow).getTime() - fetchedAtMs;
  return Math.max(0, Math.ceil((new Date(closesAt).getTime() - (Date.now() + drift)) / 1000));
}

export function formatClock(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
